import { default as DatePicker } from 'antd/es/date-picker';
import { default as Modal } from 'antd/es/modal';
import { default as Select } from 'antd/es/select';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { DATE_FORMAT, API_DATE_FORMAT } from 'utils/dayjs';
import { hasValue } from 'utils/hasValue';

import {
  useGetCustomerCurrentUser,
  usePostVirtualBankStatements,
  StatementItemKind
} from 'api';

import { useOrganisationId } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';
import { Spinner } from '@atoms/Spinner/Spinner';

import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import { CREATE_STATEMENT_MODAL_TEST_ID } from './constants';
import { CreateStatementSuccess } from './CreateStatementSuccess';

interface Props {
  accountNumber: string;
  open: boolean;
  onClose: () => void;
}
const { RangePicker } = DatePicker;

const DialogLabel = ({ text }) => {
  return (
    <label className="text-xs font-semibold text-content-secondary">
      {text}
    </label>
  );
};

export const CreateStatementDialog = ({
  accountNumber,
  open,
  onClose
}: Props) => {
  const organisationId = useOrganisationId<number>();
  const { filters } = useGlobalFilters();
  const { data } = useGetCustomerCurrentUser();
  const openNotification = useOpenNotification();

  const currentUserEmail = data?.user ? data?.user.email : 'unknown user';

  const today = useMemo(() => dayjs().endOf('day'), []);
  const [selectedFromDate, setSelectedFromDate] = useState(
    filters.dateRange.from
  );
  const [selectedToDate, setSelectedToDate] = useState(filters.dateRange.to);

  const [selectedAccountNumber, setSelectedAccountNumber] =
    useState(accountNumber);
  const [selectedKind, setSelectedKind] = useState(
    StatementItemKind.transactions
  );

  const {
    mutateAsync: createStatement,
    isSuccess: creationSuccess,
    data: newStatement,
    isPending: isCreateStatementLoading,
    reset: resetDialog
  } = usePostVirtualBankStatements();

  const onCreateStatementRequest = async (event: {
    preventDefault: () => void;
  }) => {
    event.preventDefault();
    try {
      if (hasValue(selectedFromDate) && hasValue(selectedToDate)) {
        await createStatement({
          organisationId: organisationId,
          data: {
            virtual_account_number: selectedAccountNumber,
            created_after: dayjs(selectedFromDate, DATE_FORMAT).format(
              API_DATE_FORMAT
            ),
            created_before: dayjs(selectedToDate, DATE_FORMAT).format(
              API_DATE_FORMAT
            ),
            kind: selectedKind
          }
        });
      }
    } catch (error: any) {
      onClose();
      if (error) {
        openNotification({
          kind: 'error',
          message: 'Unable to create statement'
        });
      }
    }
  };

  const onDialogClose = useCallback(() => {
    resetDialog();
    onClose();
  }, [onClose, resetDialog]);

  useEffect(() => {
    setSelectedAccountNumber(accountNumber);
  }, [accountNumber]);

  useEffect(() => {
    setSelectedFromDate(filters.dateRange.from);
    setSelectedToDate(filters.dateRange.to);
  }, [filters.dateRange.from, filters.dateRange.to]);

  // Conditions
  const showDialog = !isCreateStatementLoading && !creationSuccess;
  const showLoading = isCreateStatementLoading && !creationSuccess;
  const showSuccess =
    creationSuccess && newStatement && !isCreateStatementLoading;

  return (
    <Modal
      title={
        <div className="mb-6 flex flex-row">
          <DownloadThinIcon />
          <div className="pl-4">Create statement</div>
        </div>
      }
      open={open}
      styles={{
        mask: {
          backgroundColor: 'var(--tenant-surface-overlay)'
        },
        content: {
          paddingTop: 12,
          backgroundColor: 'rgb(255, 255, 255)'
        }
      }}
      width={336}
      onOk={onDialogClose}
      onCancel={onDialogClose}
      footer={[
        showSuccess ? (
          <PrimaryButton
            className="w-full"
            key="successButton"
            onClick={onDialogClose}
            size="large"
          >
            Done
          </PrimaryButton>
        ) : (
          <PrimaryButton
            className="w-full"
            key="exportCSVButton"
            onClick={onCreateStatementRequest}
            size="large"
          >
            Export CSV file
          </PrimaryButton>
        )
      ]}
    >
      {showDialog && (
        <div
          data-testid={CREATE_STATEMENT_MODAL_TEST_ID}
          className="my-8 flex flex-col gap-4 rounded-2xl p-0 text-sm"
        >
          <div className="flex flex-col gap-1">
            <DialogLabel text="Date range" />
            <RangePicker
              format={DATE_FORMAT}
              allowClear={false}
              maxDate={today}
              value={[
                dayjs(selectedFromDate, DATE_FORMAT),
                dayjs(selectedToDate, DATE_FORMAT)
              ]}
              onChange={(_, dateString) => {
                setSelectedFromDate(dateString[0]);
                setSelectedToDate(dateString[1]);
              }}
              placeholder={['Borderless', 'Borderless']}
              variant="outlined"
              style={{ width: 289, height: 40 }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <DialogLabel text="Account" />
            <SelectVirtualAccount
              onChangeCallback={(value) => setSelectedAccountNumber(value)}
              currentAccountNumber={selectedAccountNumber}
              allowAll={false}
              className="w-full"
              filterUnownedAccounts={true}
            />
          </div>
          <div className="flex flex-col gap-1">
            <DialogLabel text="Type" />
            <Select
              value={selectedKind}
              style={{ width: 289 }}
              onChange={(value) => setSelectedKind(value)}
              options={[
                { value: 'transactions', label: 'Transactions' },
                { value: 'charges', label: 'Charges' }
              ]}
            />
          </div>
        </div>
      )}

      {showLoading && (
        <div className="size-78 flex justify-center gap-4 rounded-b-lg p-8">
          <Spinner size="large" />
        </div>
      )}
      {showSuccess && <CreateStatementSuccess userEmail={currentUserEmail} />}
    </Modal>
  );
};
